import { DatePipe } from '@angular/common'
import {
  Component,
  ElementRef,
  Input,
  NgZone,
  OnInit,
  ViewChild,
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { AuthService } from '@auth0/auth0-angular'
import {
  ClusterPlanTypeDto,
  OrganizationDto,
} from '@camunda-cloud/cloud-node-libs'
import { CmModal } from '@camunda-cloud/common-ui-angular'
import { DropdownOptionGroup } from '@camunda-cloud/common-ui/dist/types/components/cm-dropdown/cm-dropdown'
import { first } from 'rxjs/operators'
import { FeatureService } from '../../services/feature.service'
import { UserService } from '../../services/user.service'
import { BpmnModelDto } from '../../../../../commons/BpmnModel.dto'
import { ChannelDto } from '../../../../../commons/Channel.dto'
import { ClusterDto } from '../../../../../commons/Cluster.dto'
import { ApiService } from '../../services/api.service'
import { ClusterService } from '../../services/cluster.service'
import {
  Component as MixpanelComponent,
  FrontendAnalyticEvents,
  MixpanelService,
} from '../../services/mixpanel.service'
import { NotificationService } from '../../services/notification.service'
import { ViewVisibilitiesService } from '../../services/view.visibilities.service'
import { BpmnModelerComponent } from '../bpmn-modeler/bpmn-modeler.component'
import { CreateClusterDialogComponent } from '../dialogs/create-cluster-dialog/create-cluster-dialog.component'
import { getOperateUrl } from '../taskist-operate-redirect/tasklist-operate-redirect.component'

@Component({
  selector: 'list-bpmn-models',
  templateUrl: './list-bpmn-models.component.html',
  styleUrls: ['./list-bpmn-models.component.scss'],
})
export class ListBpmnModelsComponent implements OnInit {
  public MIXPANEL_COMPONENT = MixpanelComponent.listBpmnModels
  @Input() public cluster: ClusterDto
  public models: BpmnModelDto[]

  @ViewChild('deleteModal', { read: ElementRef })
  public deleteModal: ElementRef<CmModal>

  @ViewChild('multiDeleteModal', { read: ElementRef })
  public multiDeleteModal: ElementRef<CmModal>

  @ViewChild('deployModal', { read: ElementRef })
  public deployModal: ElementRef<CmModal>

  @ViewChild('deployFailedModal', { read: ElementRef })
  public deployFailedModal: ElementRef<CmModal>

  @ViewChild('deploySuccessModal', { read: ElementRef })
  public deploySuccessModal: ElementRef<CmModal>

  @ViewChild('startInstanceModal', { read: ElementRef })
  public startInstanceModal: ElementRef<CmModal>

  @ViewChild('startInstanceFailedModal', { read: ElementRef })
  public startInstanceFailedModal: ElementRef<CmModal>

  @ViewChild('startInstanceSuccessModal', { read: ElementRef })
  public startInstanceSuccessModal: ElementRef<CmModal>

  @ViewChild('renameModal', { read: ElementRef })
  public renameModal: ElementRef<CmModal>

  public modelListColumns = [
    { name: '', width: '20px' },
    { name: 'Name', width: '3fr', ellipsis: 'right' },
    { name: 'Process Id', width: '2fr', ellipsis: 'left' },
    {
      name: 'Deployed on Cluster',
      width: 'minmax(150px, 1fr)',
      ellipsis: 'right',
    },
    { name: 'Updated By', width: '2fr', ellipsis: 'right' },
    { name: 'Updated At', width: 'minmax(200px, 2fr)' },
    { name: '', width: '20px' },
  ]
  public modelListEntities = []
  public modelListCreateHandler: () => void
  public modelListLoading: boolean = true
  public groupOptions: DropdownOptionGroup[] = []

  @ViewChild(CreateClusterDialogComponent)
  private createDialog: CreateClusterDialogComponent

  public creatingTheCluster: boolean = false

  public modelToBeRenamed: BpmnModelDto

  public modelToBeDeleted: BpmnModelDto
  public modelsToBeDeleted: BpmnModelDto[]

  public selectedClusterUuid: string
  public clusters: ClusterDto[]
  private channels: ChannelDto[]
  public planTypes: ClusterPlanTypeDto[] = []
  public deployErrorMessage: string = ''
  public deploySuccessOperateAddress: string = ''

  public selectedModel: BpmnModelDto
  public startInstanceErrorMessage: string = ''
  public startInstanceSuccessOperateAddress: string = ''

  public showZeebeCard = false
  public showGithubCard = false
  public userId: string

  public currentOrg: OrganizationDto

  constructor(
    private apiService: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private clusterService: ClusterService,
    private notificationService: NotificationService,
    private mixpanel: MixpanelService,
    public vvs: ViewVisibilitiesService,
    private datePipe: DatePipe,
    private ngZone: NgZone,
    private userService: UserService,
    private authService: AuthService,
    private featureService: FeatureService,
  ) {
    this.authService.user$.subscribe((user) => {
      if (user) {
        this.userId = user.sub
      }
    })
  }

  public ngOnInit() {
    this.currentOrg = this.route.snapshot.data.org
    this.models = this.route.snapshot.data.models
    this.clusters = this.route.snapshot.data.clusters
    this.channels = this.route.snapshot.data.channels
    this.planTypes = this.route.snapshot.data.planTypes

    this.modelListCreateHandler = () => this.createNewModel()
    this.groupOptions = [
      {
        options: [
          {
            label: 'Delete',
            isDangerous: true,
            handler: (event: any) => {
              this.openDeleteModals(
                event.detail.selectedEntities.map((entity) => entity.meta),
              )
            },
          },
        ],
      },
    ]
    this.updateList()
    this.clusterService.getClustersStatus().subscribe((clusters) => {
      this.clusters = clusters
      this.recalcAllowDeploy()
    })
    this.updateList()

    this.authService
      .getAccessTokenSilently({ ignoreCache: true })
      .subscribe((_) => {
        this.featureService.accessToken$.subscribe((accessToken) => {
          this.showZeebeCard =
            this.vvs.visibilities.featureFlags.newOnboardingExperience
              .visible && !accessToken.hasDismissedCardZeebeModeler
          this.showGithubCard =
            this.vvs.visibilities.featureFlags.newOnboardingExperience
              .visible && !accessToken.hasDismissedCardGithubSamples
          setTimeout(() => {}, 100)
        })
      })
  }

  private updateList() {
    this.modelListLoading = true

    this.modelListEntities = this.models.map((model) => {
      return {
        meta: model.uuid,
        data: [
          { type: 'image', src: this.MODEL_ICON },
          { type: 'text', content: model.name },
          { type: 'text', content: model.processId, showCopyButton: true },
          {
            type: 'text',
            content: model.deployedOnCluster
              ? model.deployedOnCluster.name
              : 'No',
          },
          {
            type: 'text',
            content: model.updatedBy,
          },
          {
            type: 'text',
            content: this.datePipe.transform(model.updated, 'medium'),
          },
          this.getContextMenu(model),
        ],
        onPress: () => {
          this.ngZone.run(() => {
            this.openModeler(model.uuid)
          })
        },
      }
    })

    this.modelListLoading = false
  }

  private getContextMenu(model: BpmnModelDto) {
    const contextMenu: { type: 'contextMenu'; options: any[] } = {
      type: 'contextMenu',
      options: [],
    }

    if (this.vvs.visibilities.modeler.save.visible) {
      contextMenu.options.push({
        options: [
          {
            label: 'Rename',
            handler: () => this.openRenameModal(model),
            isDisabled: this.vvs.visibilities.modeler.save.disabled,
          },
          {
            label: 'Export as XML',
            handler: () => {
              this.download(model)
            },
            isDisabled: this.vvs.visibilities.modeler.read.disabled,
          },
        ],
      })
    } else {
      contextMenu.options.push({
        options: [
          {
            label: 'Export as XML',
            handler: () => {
              this.download(model)
            },
            isDisabled: this.vvs.visibilities.modeler.read.disabled,
          },
        ],
      })
    }

    if (this.vvs.visibilities.modeler.deploy.visible) {
      contextMenu.options.push({
        options: [
          {
            label: 'Deploy',
            handler: () => {
              this.openDeployModal(model)
            },
            isDisabled: this.vvs.visibilities.modeler.deploy.disabled,
          },
          {
            label: 'Start Instance',
            handler: () => {
              this.openStartInstanceModal(model)
            },
            isDisabled:
              !(
                model.deployedOnCluster &&
                this.clusterHealthCheck(model.deployedOnCluster.uuid)
              ) || this.vvs.visibilities.modeler.start.disabled,
          },
        ],
      })
    }

    if (this.vvs.visibilities.modeler.delete.visible) {
      contextMenu.options.push({
        options: [
          {
            label: 'Delete',
            isDangerous: true,
            isDisabled: this.vvs.visibilities.modeler.delete.disabled,
            handler: () => {
              this.openDeleteModal(model)
            },
          },
        ],
      })
    }

    return contextMenu
  }

  private openRenameModal(model: BpmnModelDto) {
    this.modelToBeRenamed = model

    this.ngZone.run(() => {
      this.renameModal.nativeElement.open({
        preConfirmationHandler: (data) => {
          return this.apiService
            .updateBpmnModel(
              this.currentOrg.uuid,
              data.formData.newName as string,
              model.uuid,
              model.modelHash,
              model.model,
            )
            .pipe(first())
            .toPromise()
            .then(() => {
              this.ngZone.run(() => {
                this.loadModels()
              })
            })
        },
      })
    })
  }

  public openDeleteModals(models: string[]) {
    this.mixpanel.track(FrontendAnalyticEvents.LIST_MODELS_DELETE_MODEL, {
      amount: models.length,
    })

    this.modelsToBeDeleted = this.models.filter((model) =>
      models.includes(model.uuid),
    )

    this.ngZone.run(() => {
      this.multiDeleteModal.nativeElement.open().then((result) => {
        if (result.result === 'confirm') {
          this.modelListLoading = true
          this.mixpanel.track(
            FrontendAnalyticEvents.LIST_MODELS_DELETE_MODEL_CONFIRMED,
            { amount: models.length },
          )

          Promise.all(
            this.modelsToBeDeleted.map((model) =>
              this.apiService
                .deleteBpmnModel(this.currentOrg.uuid, model.uuid)
                .pipe(first())
                .toPromise(),
            ),
          ).then(() => {
            this.loadModels()
          })
        } else {
          this.mixpanel.track(
            FrontendAnalyticEvents.LIST_MODELS_DELETE_MODEL_CANCELED,
            { amount: models.length },
          )
        }
      })
    })
  }
  public openDeleteModal(model: BpmnModelDto) {
    this.mixpanel.track(FrontendAnalyticEvents.LIST_MODELS_DELETE_MODEL)
    this.modelToBeDeleted = model

    this.ngZone.run(() => {
      this.deleteModal.nativeElement.open().then((result) => {
        if (result.result === 'confirm') {
          this.modelListLoading = true
          this.mixpanel.track(
            FrontendAnalyticEvents.LIST_MODELS_DELETE_MODEL_CONFIRMED,
          )
          this.apiService
            .deleteBpmnModel(this.currentOrg.uuid, model.uuid)
            .subscribe((_) => {
              this.loadModels()
            })
        } else {
          this.mixpanel.track(
            FrontendAnalyticEvents.LIST_MODELS_DELETE_MODEL_CANCELED,
          )
        }
      })
    })
  }

  public openModeler(modelId: string) {
    this.mixpanel.track(FrontendAnalyticEvents.LIST_MODELS_OPEN_MODELER, {
      modelId,
    })
    this.router.navigate([`org/${this.currentOrg.uuid}/modeler/${modelId}`])
  }

  public createNewModel() {
    this.mixpanel.track(FrontendAnalyticEvents.LIST_MODELS_OPEN_MODELER)
    this.ngZone.run(() => {
      this.router.navigate([`org/${this.currentOrg.uuid}/modeler`])
    })
  }

  public allowDeploy: boolean = false
  public recalcAllowDeploy(newlySelectedcluster?: ClusterDto) {
    if (
      newlySelectedcluster &&
      !this.isUnsupportedAlpha(newlySelectedcluster)
    ) {
      this.selectedClusterUuid = newlySelectedcluster.uuid
    }
    if (this.selectedClusterUuid) {
      const selectedCluster = this.clusters.find(
        (clus) => clus.uuid === this.selectedClusterUuid,
      )
      this.allowDeploy = selectedCluster?.status.ready === 'Healthy'
    } else {
      this.allowDeploy = false
    }
  }
  public openDeployModal(model: BpmnModelDto) {
    this.mixpanel.track(FrontendAnalyticEvents.LIST_MODELS_DEPLOY_MODAL, {
      modelId: model.uuid,
    })

    this.selectedModel = model

    if (this.selectedModel.deployedOnCluster) {
      const deployedOnCluster = this.clusters.find(
        (clus) =>
          clus.uuid === this.selectedModel.deployedOnCluster.uuid &&
          !this.isUnsupportedAlpha(clus),
      )

      if (deployedOnCluster) {
        this.selectedClusterUuid = deployedOnCluster.uuid
      }
    } else {
      const supportedCluster = this.clusters.find(
        (clus) => !this.isUnsupportedAlpha(clus),
      )

      if (supportedCluster) {
        this.selectedClusterUuid = supportedCluster.uuid
      }
    }

    this.recalcAllowDeploy()

    this.ngZone.run(() => {
      this.deployModal.nativeElement.open().then((result) => {
        if (result.result === 'confirm') {
          this.modelListLoading = true
          this.mixpanel.track(
            FrontendAnalyticEvents.LIST_MODELS_DEPLOY_MODAL_CONFIRMED,
            { modelId: model.uuid },
          )

          if (this.clusters && this.clusters.length) {
            this.apiService
              .deployBpmnModel(
                this.currentOrg.uuid,
                this.selectedClusterUuid,
                this.selectedModel.uuid,
              )
              .subscribe(
                (_res) => {
                  this.deploySuccessOperateAddress = getOperateUrl(
                    this.clusters.find(
                      (clus) => clus.uuid === this.selectedClusterUuid,
                    ),
                  )
                  this.loadModels()
                  this.deploySuccessModal.nativeElement
                    .open()
                    .then((result) => {
                      if (result.result === 'confirm') {
                        this.openOperate()
                      }
                    })
                },
                (error) => {
                  this.deployErrorMessage = error.error
                  this.mixpanel.track(
                    FrontendAnalyticEvents.LIST_MODELS_DEPLOY_MODAL_ERROR,
                    {
                      modelId: model.uuid,
                      errorMessage: this.deployErrorMessage,
                    },
                  )
                  this.deployFailedModal.nativeElement.open()
                },
              )
          } else {
            // Wait for the close transition to finish, before we open another modal
            setTimeout(() => {
              this.openCreateClusterDialog()
            }, 300)
          }
        } else {
          this.mixpanel.track(
            FrontendAnalyticEvents.LIST_MODELS_DEPLOY_MODAL_CANCELED,
            { modelId: model.uuid },
          )
        }
      })
    })
  }

  public openStartInstanceModal(model: BpmnModelDto) {
    this.mixpanel.track(FrontendAnalyticEvents.LIST_MODELS_START_MODAL, {
      modelId: model.uuid,
    })
    this.selectedModel = model
    this.ngZone.run(() => {
      this.startInstanceModal.nativeElement.open().then((result) => {
        if (result.result === 'confirm') {
          this.mixpanel.track(
            FrontendAnalyticEvents.LIST_MODELS_START_MODAL_CONFIRMED,
            {
              modelId: model.uuid,
            },
          )
          this.apiService
            .startInstanceBpmnModel(
              this.currentOrg.uuid,
              model.deployedOnCluster.uuid,
              model.uuid,
              result.formData.startInstanceMetadata as string,
            )
            .subscribe(
              (_res) => {
                this.deploySuccessOperateAddress = getOperateUrl(
                  this.clusters.find(
                    (clus) => clus.uuid === model.deployedOnCluster.uuid,
                  ),
                )
                this.startInstanceSuccessModal.nativeElement
                  .open()
                  .then((result) => {
                    if (result.result === 'confirm') {
                      this.openOperate()
                    }
                  })
              },
              (error) => {
                this.startInstanceErrorMessage = error.error

                this.mixpanel.track(
                  FrontendAnalyticEvents.LIST_MODELS_START_MODAL_ERROR,
                  {
                    modelId: model.uuid,
                    errorMessage: this.startInstanceErrorMessage,
                  },
                )
                this.startInstanceFailedModal.nativeElement.open()
              },
            )
        } else {
          this.mixpanel.track(
            FrontendAnalyticEvents.LIST_MODELS_START_MODAL_CANCELED,
            {
              modelId: model.uuid,
            },
          )
        }
      })
    })
  }

  private loadModels() {
    this.apiService.listBpmnModels(this.currentOrg.uuid).subscribe((models) => {
      this.models = models
      this.updateList()
    })
  }

  // eslint-disable-next-line class-methods-use-this
  public download(model: BpmnModelDto) {
    this.mixpanel.track(FrontendAnalyticEvents.LIST_MODELS_DOWNLOAD, {
      modelId: model.uuid,
    })
    const a = document.createElement('a')
    a.download = `${model.name}.bpmn`
    a.href = window.URL.createObjectURL(
      new Blob([model.model], { type: 'text/xml' }),
    )
    a.click()
    window.URL.revokeObjectURL(a.href)
  }

  public navigateToClusterList() {
    this.router.navigate([`org/${this.currentOrg.uuid}`])
  }

  public getHealthyClusters() {
    return this.clusters.filter(
      (clus) => clus.status.ready && clus.status.ready === 'Healthy',
    )
  }

  public openOperate() {
    this.mixpanel.track(FrontendAnalyticEvents.LIST_MODELS_OPEN_OPERATE)
    window.open(this.deploySuccessOperateAddress, '_blank')
  }

  public isValidJson = {
    type: 'custom',
    validator: (value: string) => {
      try {
        JSON.parse(value)
        return { isValid: true }
      } catch (error) {
        return {
          isValid: false,
          type: 'invalid',
          message: 'Please enter a valid JSON Object.',
        }
      }
    },
  }

  public openCreateClusterDialog() {
    this.mixpanel.track(
      FrontendAnalyticEvents.LIST_MODELS_CREATE_CLUSTER_DIALOG,
    )
    this.creatingTheCluster = true
    this.createDialog
      .open({
        currentOrg: this.currentOrg,
        clusters: this.clusters,
        channels: this.channels,
        planTypes: this.planTypes,
      })
      .catch((_error) => {
        this.notificationService.enqueueNotification({
          headline: 'Cluster could not be created',
          appearance: 'error',
        })
        this.creatingTheCluster = false
      })
      .then((createdCluster) => {
        if (createdCluster) {
          this.clusterService.getClustersStatus().subscribe((clusters) => {
            this.clusters = clusters
            if (this.clusters.length > 0) {
              this.creatingTheCluster = false
            }
            if (
              this.clusters.filter(
                (clus) => clus.status.ready && !this.isUnsupportedAlpha(clus),
              ).length > 0 &&
              !this.selectedClusterUuid
            ) {
              this.selectedClusterUuid = this.clusters.filter(
                (clus) => clus.status.ready && !this.isUnsupportedAlpha(clus),
              )[0].uuid
            }
            this.recalcAllowDeploy()
          })
          this.openDeployModal(this.selectedModel)
        } else {
          this.creatingTheCluster = false
        }
      })
  }

  public clusterHealthCheck(clusterId: string) {
    const cluster = this.clusters.find((clus) => clus.uuid === clusterId)
    return cluster && cluster.status.ready === 'Healthy'
  }

  public async dismissZeebeModeler() {
    this.showZeebeCard = false
    await this.userService
      .dismissCardZeebeModeler(this.userId)
      .pipe(first())
      .toPromise()
  }

  public async dismissGithubSamples() {
    this.showGithubCard = false
    await this.userService
      .dismissCardGithubSamples(this.userId)
      .pipe(first())
      .toPromise()
  }

  private MODEL_ICON = `data:image/svg+xml;base64,${btoa(
    unescape(
      encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="20" height="20" viewBox="0 0 20 20" fill="#62626E">
      <path fill-rule="evenodd" d="M10.110775,0.0001025 L10.1962,0.00146 C10.4789,0.0069 10.7587,0.02407 11.0352,0.0525 C11.2515,0.07475 11.4226,0.24185 11.4596,0.45613 L11.4596,0.45613 L11.7329,2.0413 C11.7672,2.24039 11.9184,2.39781 12.1133,2.45088 C12.7291,2.61852 13.3148,2.85909 13.8606,3.16273 C14.0351,3.25982 14.2504,3.25409 14.4136,3.13892 L14.4136,3.13892 L15.7159,2.21964 C15.8934,2.0943 16.1325,2.09706 16.3012,2.23411 C16.8383,2.67046 17.3292,3.16141 17.7655,3.69851 C17.9026,3.8672 17.9053,4.10621 17.78,4.28378 L17.78,4.28378 L16.8808,5.55766 C16.7639,5.7232 16.7599,5.94217 16.8611,6.11771 C17.1814,6.67321 17.4358,7.27153 17.6136,7.90197 C17.668,8.09477 17.8246,8.24377 18.0219,8.27777 L18.0219,8.27777 L19.5435,8.54007 C19.7578,8.57707 19.9249,8.74807 19.9471,8.96437 C19.9718,9.20307 19.9877,9.44227 19.9951,9.68177 C19.9984,9.78727 20,9.89327 20,9.99957 C20,10.08487 19.999,10.16997 19.9968,10.25487 L19.9968,10.25487 L19.9948,10.32597 C19.9872,10.56447 19.9712,10.80077 19.9471,11.03477 C19.9249,11.25107 19.7578,11.42207 19.5435,11.45907 L19.5435,11.45907 L18.0461,11.71717 C17.845,11.75327 17.6888,11.90427 17.6363,12.09857 C17.4622,12.74287 17.2083,13.35437 16.8859,13.92187 C16.7864,14.09697 16.7911,14.31447 16.9073,14.47897 L16.9073,14.47897 L17.78,15.71537 C17.9053,15.89297 17.9026,16.13197 17.7655,16.30067 C17.3292,16.83777 16.8382,17.32867 16.3012,17.76507 C16.1325,17.90207 15.8934,17.90487 15.7159,17.77947 L15.7159,17.77947 L14.4717,16.90137 C14.3081,16.79047 14.0947,16.78737 13.9224,16.88537 C13.3549,17.20777 12.7434,17.46167 12.0991,17.63577 C11.9038,17.68857 11.7521,17.84617 11.7177,18.04557 L11.7177,18.04557 L11.4596,19.54297 C11.4226,19.75727 11.2515,19.92437 11.0352,19.94667 C10.3469,20.01787 9.6533,20.01787 8.9649,19.94667 C8.7486,19.92437 8.5776,19.75727 8.5406,19.54307 L8.5406,19.54307 L8.2763,18.01067 C8.2388,17.81837 8.0918,17.66647 7.90248,17.61307 C7.58827,17.52447 7.28203,17.41687 6.9851,17.29147 C6.68325,17.16927 6.38745,17.02757 6.09964,16.86637 C5.92676,16.76957 5.71346,16.77377 5.55047,16.88577 L5.55047,16.88577 L4.28424,17.77957 C4.10667,17.90487 3.86766,17.90207 3.69897,17.76507 C3.48787,17.59357 3.2839,17.41367 3.08757,17.22577 C3.03424,17.17477 2.98135,17.12307 2.92893,17.07067 L2.92893,17.07067 L2.76275,16.90047 C2.57895,16.70777 2.40278,16.50767 2.2347,16.30087 C2.09748,16.13217 2.09436,15.89347 2.21976,15.71577 L2.21976,15.71577 L3.096,14.47447 C3.21271,14.30907 3.21692,14.09047 3.1161,13.91497 C2.78364,13.33627 2.53078,12.72437 2.35753,12.09507 C2.30406,11.90087 2.14691,11.75047 1.94841,11.71627 L1.94841,11.71627 L0.45705,11.45917 C0.24287,11.42227 0.07581,11.25127 0.05343,11.03507 C-0.01781,10.34677 -0.01781,9.65247 0.05343,8.96407 C0.0758,8.74797 0.24286,8.57697 0.45705,8.54007 L0.45705,8.54007 L1.94841,8.28287 C2.14691,8.24867 2.30406,8.09827 2.35753,7.90409 C2.53078,7.27483 2.78364,6.66291 3.1161,6.08424 C3.21692,5.90875 3.21271,5.69009 3.096,5.52474 L3.096,5.52474 L2.21973,4.28337 C2.09434,4.10573 2.09745,3.86702 2.23467,3.69834 C2.40276,3.49145 2.57895,3.2914 2.76276,3.09869 L2.76276,3.09869 L2.92893,2.9285 C2.98135,2.87609 3.03423,2.82438 3.08755,2.77338 C3.28389,2.58552 3.48786,2.40557 3.69897,2.23407 C3.86766,2.09702 4.10667,2.09427 4.28424,2.21961 L4.28424,2.21961 L5.55312,3.11529 C5.71576,3.22544 5.92769,3.22911 6.09964,3.13281 C6.67113,2.81273 7.27409,2.56955 7.89339,2.40325 C8.0891,2.3507 8.241,2.19298 8.2755,1.99329 L8.2755,1.99329 L8.5406,0.45612 C8.5776,0.24184 8.7486,0.07474 8.9649,0.05249 C9.2781,0.02007 9.5922,0.00285 9.9067,-5.10702591e-14 L9.9067,-5.10702591e-14 L10.110775,0.0001025 L10.110775,0.0001025 Z M10,3.99957 C6.68629,3.99957 4,6.68586 4,9.99957 C4,13.31327 6.68629,15.99957 10,15.99957 C13.3137,15.99957 16,13.31327 16,9.99957 C16,6.68586 13.3137,3.99957 10,3.99957 Z M8,7.13461 C8,6.28677 8.9889,5.82362 9.6402,6.36639 L9.6402,6.36639 L13.0781,9.23137 C13.5579,9.63117 13.5579,10.36797 13.0781,10.76777 L13.0781,10.76777 L9.6402,13.63277 C8.9889,14.17557 8,13.71237 8,12.86457 L8,12.86457 Z"/>
  </svg>`),
    ),
  )}`

  // eslint-disable-next-line class-methods-use-this
  public isUnsupportedAlpha(cluster: ClusterDto) {
    return BpmnModelerComponent.isUnsupportedAlpaVersion(
      cluster.generation.versions.zeebe,
    )
  }
}
